import {
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { Subscription } from 'rxjs';
import {
  IQuestionnaireChoice,
  IQuestionnaireQuestion,
} from 'src/app/interfaces/Questionnaire/IQuestionnaire';

interface FimScoreField {
  controlName: string;
  maxScore: number;
  isTotal: boolean;
  // The items it adds up, or for the total the subtotals it adds up.
  parts: string[];
}

// The points are the level in "(n)" at the start of the answer, from
// "(7) Complete Independence" down to "(1) Total Assist": never 0.
const FIM_POINTS_REGEX = /^\s*\(\s*(\d+)\s*\)/;
const FIM_MIN_LEVEL = 1;
const FIM_MAX_LEVEL = 7;

// Titles separate the instruction with "||" or "‖".
const FIM_TITLE_SEPARATOR = /\|\||‖/;

// In calculation order: the total comes after the subtotals it adds up.
const FIM_SCORE_FIELDS: FimScoreField[] = [
  // Motor: A-F self-care, G-H sphincter control, I-K transfers, L-M locomotion
  {
    controlName: 'question883',
    maxScore: 91,
    isTotal: false,
    parts: [
      'question866',
      'question867',
      'question868',
      'question869',
      'question870',
      'question871',
      'question873',
      'question874',
      'question876',
      'question877',
      'question878',
      'question880',
      'question882',
    ],
  },
  // Cognitive: N-O communication, P-R social cognition
  {
    controlName: 'question893',
    maxScore: 35,
    isTotal: false,
    parts: [
      'question885',
      'question887',
      'question890',
      'question891',
      'question892',
    ],
  },
  // TOTAL FIM, the same number as the backend score
  {
    controlName: 'question894',
    maxScore: 126,
    isTotal: true,
    parts: ['question883', 'question893'],
  },
];

// How L, N and O were done, keyed by the item each one follows. All three are
// titled "Mode" and their answers have no "(n)": they are never scored.
const FIM_MODES: Record<string, string> = {
  question880: 'question881', // L. Walk/Wheelchair: W, C or B
  question885: 'question886', // N. Comprehension: A, V or B
  question887: 'question888', // O. Expression: V, N or B
};

@Component({
  selector: 'app-fim-questions',
  templateUrl: './fim-questions.component.html',
  styleUrl: './fim-questions.component.css',
  standalone: false,
})
export class FimQuestionsComponent implements OnInit, OnDestroy {
  @Input() elements: IQuestionnaireQuestion[] = [];
  // The page title holds the scale's heading and instruction.
  @Input() pageTitle = '';
  @Input() formGroup!: FormGroup;
  @Input() readonlyMode = false;

  private sub?: Subscription;

  constructor(private readonly cdr: ChangeDetectorRef) {}

  ngOnInit(): void {
    this.sub = this.formGroup.valueChanges.subscribe(() => {
      this.recalculateScores();
    });
    this.recalculateScores();
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  onRadioClick(controlName: string, value: any): void {
    if (this.readonlyMode) return;
    const control = this.formGroup.get(controlName);
    if (!control) return;

    if (control.value === value) {
      control.setValue(null);
    }
  }

  getQuestionTitle(title?: string): string {
    return title?.split(FIM_TITLE_SEPARATOR)[0]?.trim() ?? '';
  }

  getQuestionInstruction(title?: string): string {
    return title?.split(FIM_TITLE_SEPARATOR)[1]?.trim() ?? '';
  }

  // A mode is shown inside the row of the item it belongs to.
  isModeElement(element: IQuestionnaireQuestion): boolean {
    return Object.values(FIM_MODES).includes(element.name);
  }

  getModeElement(
    element: IQuestionnaireQuestion,
  ): IQuestionnaireQuestion | undefined {
    const modeName = FIM_MODES[element.name];
    return modeName
      ? this.elements.find((el) => el.name === modeName)
      : undefined;
  }

  isScoreElement(element: IQuestionnaireQuestion): boolean {
    return element.type === 'text' && !!this.getScoreField(element.name);
  }

  isTotalScoreElement(element: IQuestionnaireQuestion): boolean {
    return this.getScoreField(element.name)?.isTotal ?? false;
  }

  // The template has no maximums, so the UI supplies them.
  getMaxScore(controlName: string): number | null {
    return this.getScoreField(controlName)?.maxScore ?? null;
  }

  getScoreDisplay(controlName: string): string {
    const value = this.formGroup.get(controlName)?.value;
    return value === null || value === undefined || value === ''
      ? '0'
      : String(value);
  }

  private getScoreField(controlName: string): FimScoreField | undefined {
    return FIM_SCORE_FIELDS.find((field) => field.controlName === controlName);
  }

  private recalculateScores(): void {
    const scores = new Map<string, number | null>();

    for (const field of FIM_SCORE_FIELDS) {
      const score = this.sumParts(field.parts, scores);
      scores.set(field.controlName, score);
      this.setLockedScore(field.controlName, score);
    }

    this.cdr.detectChanges();
  }

  // Live sum of the parts answered so far, or null while none is, so an
  // untouched subtotal stays empty and is not saved.
  private sumParts(
    parts: string[],
    scores: Map<string, number | null>,
  ): number | null {
    const points = parts
      .map((part) =>
        scores.has(part)
          ? (scores.get(part) ?? null)
          : this.getSelectedPoints(part),
      )
      .filter((point): point is number => point !== null);

    return points.length ? points.reduce((sum, point) => sum + point, 0) : null;
  }

  // Points come from the selected choice text, never from its position.
  private getSelectedPoints(controlName: string): number | null {
    const selectedValue = this.formGroup.get(controlName)?.value;
    if (
      selectedValue === null ||
      selectedValue === undefined ||
      selectedValue === ''
    ) {
      return null;
    }

    const choice = (
      this.elements?.find((el) => el.name === controlName)?.choices ?? []
    ).find((c: IQuestionnaireChoice) => c.value === selectedValue);
    const match = (choice?.text ?? '').match(FIM_POINTS_REGEX);
    const level = match ? Number(match[1]) : null;

    return level !== null && level >= FIM_MIN_LEVEL && level <= FIM_MAX_LEVEL
      ? level
      : null;
  }

  private setLockedScore(controlName: string, score: number | null): void {
    const control = this.formGroup.get(controlName);
    if (!control) return;

    control.setValue(score === null ? '' : String(score), {
      emitEvent: false,
    });
    control.disable({ emitEvent: false });
  }
}
